import { get } from '../../utils';
import LazyLoadingScript from './LazyLoadingScript';
import DeviceDetector from './DeviceDetector';
import MessageBusTopics from '../../common/MessageBusTopics';

const X_SELL_GAMING_LOBBY_COMPONENT = {
    hash: 'top-games',
    type: 'GAMING_LOBBY',
};
const X_SELL_GAMING_LOBBY_JACKPOTS_COMPONENT = {
    hash: 'top-games-jackpots',
    type: 'GAMING_LOBBY',
};

export const XSELL_SELECTOR_ID = 'games-lobby-container';
export const X_SELL_PREVIEW_COOKIE = 'XSELL_PREVIEW=true';
export const X_SELL_HASH_COMPONENTS = [X_SELL_GAMING_LOBBY_COMPONENT, X_SELL_GAMING_LOBBY_JACKPOTS_COMPONENT];

let isXsellIntegratorLoaded;
let isLoadingInProgress;
let component_id;

/**
 * Scans window looking for injected xsell component urls
 * @returns {string[]}
 * @private
 */
function getXsellUrls() {
    const xsellScripts = window.xsellViewData && window.xsellViewData.scripts || {};
    const isXsellPreviewEnabled = document.cookie.indexOf(X_SELL_PREVIEW_COOKIE) !== -1;
    if ( isXsellPreviewEnabled && xsellScripts.preview ) {
        return xsellScripts.preview;
    }

    if ( xsellScripts.live ) {
        return xsellScripts.live;
    }

    // guardian
    return [];
}

/**
 * Prepares public assets path for xsell's webpack bundle async chunks
 * @param {string[]} urls
 * @returns {boolean}
 * @private
 */
function setUpXsellAssetsPath(urls) {
    if (urls && urls[0]){
        const basePathRegexp = new RegExp(/^.*\//);
        const rootUrl = urls[0];
        const matches = basePathRegexp.exec(rootUrl);
        const basePath = matches[0];
        window.__getXSellAssetsPublicPath__ = () => basePath;

        return true;
    }
    return false;
}

/**
 * @param hash
 * @returns {string}
 * @private
 */
const sanitizeHashValue = (hash) => hash.replace('#', '');

/**
 * Verify state of window embeddedEnabled property
 * @returns {boolean}
 * @private
 */
function isXsellEmbeddedEnabled() {
    const embeddedEnabledPropPath = 'whStatic.config.crossSell.embeddedEnabled';
    return get(window, embeddedEnabledPropPath);
}

/**
 * Find the component by provided hash matching with component hash
 * @param {string} hash
 * @returns {object | undefined}
 * @private
 */
function findXsellComponentByHash(hash) {
    const hashValue = sanitizeHashValue(hash);
    return X_SELL_HASH_COMPONENTS.find(item => item.hash === hashValue);
}

/**
 * Lazy loading script method wrapped in promise
 * @param {string[]} urls
 * @returns {Promise<any>}
 * @private
 */
function lazyLoadScript(urls) {
    return new Promise(resolve => {
        LazyLoadingScript.loadAllOneByOne(urls, resolve);
    });
}

/**
 * @param {string[]} xSellUrls
 * @param {function} wrappedFunc
 * @returns {Promise<void>}
 * @private
 */
async function withPreloader({ xSellUrls, wrappedFunc = async () => {} }) {
    await wrappedFunc(xSellUrls);
}

/**
 * Load xsell-integrator bundle
 * @param {function} loader
 * @returns {Promise<void>}
 * @private
 */
async function loadXsellIntegrator({ loader }) {
    const xSellUrls = getXsellUrls();
    if (xSellUrls.length === 0) {
        return;
    }
    return new Promise(async (resolve) => {
        if(isXsellIntegratorLoaded || isLoadingInProgress) {
            return resolve(isXsellIntegratorLoaded);
        }

        window.WH.messageBus.subscribe(MessageBusTopics.XSELL_INTEGRATOR_LOADED, () => {
            
            isLoadingInProgress = false;
            isXsellIntegratorLoaded = true;

            // On Success Events Listeners
            window.WH.messageBus.subscribe(MessageBusTopics.XSELL_INTEGRATOR_EVENT_INIT_SUCCESS, onSuccessXsellEvent);
            window.WH.messageBus.subscribe(MessageBusTopics.XSELL_INTEGRATOR_EVENT_UPDATE_SUCCESS, onSuccessXsellEvent);
            window.WH.messageBus.subscribe(MessageBusTopics.XSELL_INTEGRATOR_EVENT_SHUTDOWN_SUCCESS, shutdownXSell);
            window.WH.messageBus.subscribe(MessageBusTopics.XSELL_INTEGRATOR_EVENT_SCROLL, onScroll);

            // On Error Events listeners
            window.WH.messageBus.subscribe(MessageBusTopics.XSELL_INTEGRATOR_EVENT_SHUTDOWN_ERROR, shutdownXSell);
            window.WH.messageBus.subscribe(MessageBusTopics.XSELL_INTEGRATOR_EVENT_INIT_ERROR, shutdownXSell);
            window.WH.messageBus.subscribe(MessageBusTopics.XSELL_INTEGRATOR_EVENT_UPDATE_ERROR, shutdownXSell);
            resolve(true);
        });

        if(setUpXsellAssetsPath(xSellUrls)) {
            await withPreloader({ xSellUrls, wrappedFunc: loader });
        }
    });
}

function onScroll(topic, scrollDir) {
    if (!DeviceDetector.isMobile()) {
        return;
    }

    let container = window.document.querySelector('#games-lobby-container');
    let header = window.document.querySelector('.sb-header');
    if (!container || !header) {
        return;
    }

    if (scrollDir === 'up') {
        container.style.marginTop = '80px';
        header.classList.remove('sb-header--collapsed');
    }
    else if (scrollDir === 'down') {
        container.style.marginTop = '44px';
        header.classList.add('sb-header--collapsed');
    }
}

/**
 *  Blocks the scroll in the body
 */
function blockBody() {
    document.body.style.overflowY = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.height = window.innerHeight;
}

/**
 *  Unblocks the scroll in the body
 */
function unblockBody() {
    document.body.style.overflowY = '';
    document.body.style.position = '';
    document.body.style.height = '';
}

/**
 *  Adds a DOM container for Xsell lobby and appends to document.body
 */
function addXsellDOMElement() {
    const isJurisdictionIT = window.whStatic.config.jurisdiction === '.it';
    const isJurisdictionES = window.whStatic.config.jurisdiction === '.es';

    const element = document.createElement('div');
    element.id = XSELL_SELECTOR_ID;
    element.className = 'xs-modal xs-modal--open';

    document.body.appendChild(element);

    if (isJurisdictionIT || isJurisdictionES) {
        if (!DeviceDetector.isMobile() && (!DeviceDetector.isNativeItaly() || !DeviceDetector.isNativeSpain())) {
            document.getElementById(XSELL_SELECTOR_ID).style.marginTop = '80px';
        } 
        if (DeviceDetector.isNativeItaly() || DeviceDetector.isNativeSpain()) {
            document.getElementById(XSELL_SELECTOR_ID).style.marginTop = '0px';
        } else {
            document.getElementById(XSELL_SELECTOR_ID).style.marginTop = '80px';
        }
        document.getElementById(XSELL_SELECTOR_ID).style.zIndex = '20';
    }
    blockBody();
}

/**
 * Shutdowns the Xsell lobby, performs a cleanup task. Can be used as MB event handler
 */
function shutdownXSell() {
    const element = document.getElementById(XSELL_SELECTOR_ID);
    unblockBody();
    if(element) {
        element.remove();
    }

    component_id = null;

    // reset hash in case it's still there
    if(findXsellComponentByHash(window.location.hash)) {
        window.location.hash = '';
    }
}

/**
 * Sets the component_id to the provided value, function to be used as MB event handler
 * @param topic
 * @param id
 */
function onSuccessXsellEvent(topic, { id }) {
    component_id = id;
}

/**
 * Loads the Xsell lobby and decides whether to update or init
 * @param newComponent
 * @param loaderOptions
 * @returns {Promise<void>}
 */
async function loadXsellComponent(newComponent, loaderOptions) {
    const { type, hash: currentTab } = newComponent;
    const payload = { currentTab };

    if(component_id) {
        window.WH.messageBus.publish(MessageBusTopics.XSELL_INTEGRATOR_COMPONENT_UPDATE, { id: component_id, payload });

    } else {
        const hasBeenLoaded = await loadXsellIntegrator(loaderOptions);

        if (hasBeenLoaded) {
            window.WH.messageBus.publish(MessageBusTopics.BETSLIP_CLOSE_DRAWERS);

            addXsellDOMElement();
            const data = { component: { type }, socket: { selector: `#${XSELL_SELECTOR_ID}` }, payload };

            window.WH.messageBus.publish(MessageBusTopics.XSELL_INTEGRATOR_COMPONENT_INIT, data);
        }
    }
}

/**
 * Initialisation of xsell-integrator loader
 * @param {function} loader
 * @returns {Promise<void>}
 * @public
 */
async function init(loader = lazyLoadScript) {
    if (isXsellEmbeddedEnabled()) {
        isXsellIntegratorLoaded = false;
        isLoadingInProgress = false;
        component_id = null;

        // initial load for games lobby
        const componentOnInit = findXsellComponentByHash(window.location.hash);
        if (componentOnInit){
            await loadXsellComponent(componentOnInit, { loader });
        }

        // Sportsbook binds message-bus AppJs.hashChange to window hashchange event
        // the hash value is used to trigger xsell-integrator features
        window.WH.messageBus.subscribe(MessageBusTopics.APP_JS_HASH_CHANGE, async (topic, { hash }) => {
            const component = findXsellComponentByHash(hash);

            if(component){
                // Using hammer here, let's only load when location.hash change happen
                // this is needed because some links only trigger MB hash event, they don't actually change the url hash
                if(sanitizeHashValue(window.location.hash) !== hash) {
                    window.location.hash = hash; // updates url and triggers a new hash change event
                    return;
                }

                await loadXsellComponent(component, { loader });
            } else if(component_id) {
                // Requests Shutdown
                window.WH.messageBus.publish(MessageBusTopics.XSELL_INTEGRATOR_COMPONENT_SHUTDOWN,
                    { id: component_id });
            }
        });

        // After bet placement in old-betslip, bet-receipt banners should be initialised
        // bet-receipt requires xsell-integrator bundle to be loaded
        window.WH.messageBus.subscribe(MessageBusTopics.XSELL_INJECT_BET_RECEIPT_APP, async () => {
            const hasBeenLoaded = await loadXsellIntegrator({ loader });
            if (hasBeenLoaded) {
                window.WH.messageBus.publishSticky(MessageBusTopics.XSELL_INTEGRATOR_BET_RECEIPT_INIT, true);
                
            }
        });
    }
}

const api = { init };

export default api;
